/**
 * Utilities for calculating text and watermark contrast in Luminary scenes.
 * Analyzes the contrast between text assets/watermarks and their backgrounds.
 */

import type { Slide, TextAsset } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';
import type { MediaAsset } from '@lumen5/luminary/core/asset/MediaAsset';

import QueryString from 'js/src/libs/querystring';
import type { RGB } from 'js/src/utils/contrast-utils';
import {
    averageColor,
    calculateContrastRatio,
    extractColorsFromImageData,
} from 'js/src/utils/contrast-utils';

// Check if debug mode is enabled via query string
const DEBUG_CONTRAST = QueryString.debugContrast === 'true';

export interface TextContrastResult {
    contrastRatio: number;
    textColor: RGB;
    backgroundColor: RGB;
    textAssetId: string;
    textAssetName: string;
    textContent: string;
}

export interface WatermarkContrastResult {
    contrastRatio: number;
    watermarkColor: RGB;
    backgroundColor: RGB;
    watermarkPosition: string;
    watermarkOpacity: number;
}

/**
 * Gets the fill color from a text asset.
 * Returns the primary fill color as RGB.
 */
function getTextAssetColor(textAsset: TextAsset): RGB {
    // Get the fill color from the text asset
    // TextAssets may have color stored in different ways:
    // - textAsset.fillColor (direct property, decimal number)
    // - normalStyle.fill (palette index)
    // - A hex string
    // - An RGB object

    // get fill color
    const normalStyle = textAsset.normalStyle;
    const fillColor = normalStyle?.fill;

    // If it's a number, it's always a decimal color value (e.g., 0 for black, 14884386 for red, 16777215 for white)
    if (fillColor === undefined || fillColor === null) {
        throw new Error('Text asset has no fill color defined');
    }

    // Convert decimal to RGB by extracting the RGB components
    return {
        r: Math.floor(fillColor / 65536) % 256,
        g: Math.floor(fillColor / 256) % 256,
        b: fillColor % 256,
    };
}

/**
 * Renders a scene to a canvas with a specific text asset colored magenta,
 * then extracts the background colors from the text asset's bounds.
 * The magenta color is filtered out when sampling to get only background pixels.
 * This approach preserves all text graphics (backgrounds, strokes, shadows, etc.).
 */
async function getBackgroundColorsForTextAsset(
    scene: Slide,
    textAsset: TextAsset,
    canvas: HTMLCanvasElement,
): Promise<RGB[]> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    // First render the scene once to ensure bounds are calculated
    await scene.renderPreviewToCanvas(canvas, {
        subsceneIndex: 0,
    });

    // Get the bounds of the text asset using the same logic as getNormalizedAssetBounds
    // Use approximateMaxBounds if user changed max width, otherwise use boundsWithoutAnimation
    let bounds;
    if (textAsset.hasUserChangedMaxWidth) {
        const approxBounds = textAsset.approximateMaxBounds;
        const normalBounds = textAsset.boundsWithoutAnimation;
        bounds = {
            x: approxBounds.x,
            y: normalBounds.y,
            width: approxBounds.width,
            height: normalBounds.height,
        };
    } else {
        bounds = textAsset.boundsWithoutAnimation;
    }

    let { x, y, width: assetWidth, height: assetHeight } = bounds;

    // Scale from reference resolution to output resolution
    const scaleX = width / refWidth;
    const scaleY = height / refHeight;

    x = x * scaleX;
    y = y * scaleY;
    assetWidth = assetWidth * scaleX;
    assetHeight = assetHeight * scaleY;

    // Temporarily change the text color to magenta (a color unlikely to appear in designs)
    // This preserves all text graphics (backgrounds, strokes, shadows) while making
    // the actual text characters filterable when sampling background colors.
    // We keep the original text content to maintain proper text bounds and rendering.
    const originalFillColor = textAsset.normalStyle.fill;
    const markerColor = 16711935; // Magenta (0xFF00FF = 255 * 65536 + 0 * 256 + 255)
    textAsset.normalStyle.fill = markerColor;

    try {
        // Render the scene with magenta-colored text
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
        });

        // Get the canvas context
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        // If bounds are zero or invalid (e.g., in test environments), use the full canvas
        // In production, text assets from scene designs will have proper bounds
        if (assetWidth <= 0 || assetHeight <= 0) {
            x = 0;
            y = 0;
            assetWidth = width;
            assetHeight = height;
        }

        // Extract the pixel data from the text asset's bounds
        // Clamp the bounds to ensure they're within the canvas
        const clampedX = Math.max(0, Math.floor(x));
        const clampedY = Math.max(0, Math.floor(y));
        const clampedWidth = Math.min(width - clampedX, Math.ceil(assetWidth));
        const clampedHeight = Math.min(height - clampedY, Math.ceil(assetHeight));

        if (clampedWidth <= 0 || clampedHeight <= 0) {
            throw new Error('Text asset bounds are out of canvas bounds');
        }

        const imageData = ctx.getImageData(clampedX, clampedY, clampedWidth, clampedHeight);

        // debugging
        if (DEBUG_CONTRAST) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = clampedWidth;
            tempCanvas.height = clampedHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (tempCtx) {
                tempCtx.putImageData(imageData, 0, 0);
                const patchDataURL = tempCanvas.toDataURL();
                // eslint-disable-next-line no-console
                console.log(
                    `[getBackgroundColorsForTextAsset] Sampled patch for "${textAsset.name}" (${clampedWidth}x${clampedHeight} at ${clampedX},${clampedY}):`,
                    {
                        dataURL: patchDataURL,
                    },
                );
            }
        }

        // Sample every 10th pixel for performance (can be adjusted)
        const sampleRate = 10;
        const colors = extractColorsFromImageData(imageData, sampleRate);

        // Filter out the marker color (magenta) that we used for the text
        // Allow for slight variations due to anti-aliasing/rendering
        const filteredColors = colors.filter((color) => {
            // Check if this color is close to magenta (255, 0, 255)
            // Allow a tolerance of 10 for anti-aliasing
            const isMagenta =
                Math.abs(color.r - 255) < 10 &&
                Math.abs(color.g - 0) < 10 &&
                Math.abs(color.b - 255) < 10;
            return !isMagenta;
        });

        return filteredColors;
    } finally {
        // Restore the original text color
        textAsset.normalStyle.fill = originalFillColor;
    }
}

/**
 * Checks if there are any media assets that overlap with the given bounds.
 * Uses approximateMaxBounds from getFreelyPositionedParent() like the validation code does.
 */
function hasOverlappingMediaAssets(scene: Slide, textAsset: TextAsset): boolean {
    // Get text bounds using the same approach as validation code
    const textBounds = textAsset.getFreelyPositionedParent().approximateMaxBounds;
    const textLeft = Math.floor(textBounds.x);
    const textRight = Math.ceil(textBounds.x + textBounds.width);
    const textTop = Math.floor(textBounds.y);
    const textBottom = Math.ceil(textBounds.y + textBounds.height);

    // Get all media assets
    const mediaAssets = scene
        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
        .filter((asset) => asset.isEditable);

    // Check if any media asset overlaps with text bounds
    for (const mediaAsset of mediaAssets) {
        const mediaBounds = mediaAsset.getFreelyPositionedParent().approximateMaxBounds;
        const mediaLeft = Math.floor(mediaBounds.x);
        const mediaRight = Math.ceil(mediaBounds.x + mediaBounds.width);
        const mediaTop = Math.floor(mediaBounds.y);
        const mediaBottom = Math.ceil(mediaBounds.y + mediaBounds.height);

        // Check for overlap using rectangle intersection (same logic as validation code)
        const deltaX = Math.min(textRight, mediaRight) - Math.max(textLeft, mediaLeft);
        const deltaY = Math.min(textBottom, mediaBottom) - Math.max(textTop, mediaTop);
        const overlaps = deltaX > 0 && deltaY > 0;

        if (overlaps) {
            return true;
        }
    }

    return false;
}

/**
 * Calculates the contrast ratio between a text asset and its background in a scene.
 *
 * This function:
 * 1. Checks if there are any media assets overlapping with the text (skips if none)
 * 2. Temporarily changes the text color to magenta (preserving all text graphics)
 * 3. Renders the scene to a canvas
 * 4. Extracts colors from the text asset's bounds, filtering out magenta pixels
 * 5. Calculates the average background color
 * 6. Gets the original text color
 * 7. Calculates and returns the contrast ratio using WCAG 2.0 formula
 *
 * @param scene - The Luminary scene containing the text asset
 * @param textAsset - The text asset to analyze
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @returns Promise resolving to TextContrastResult with contrast information, or null if no overlapping media
 * @throws Error if text asset is not a valid text asset
 */
export async function calculateTextContrast(
    scene: Slide,
    textAsset: TextAsset,
    canvas: HTMLCanvasElement,
): Promise<TextContrastResult | null> {
    // Verify this is a text asset
    if (!VideoData.isTextAsset(textAsset)) {
        throw new Error('Asset is not a text asset');
    }

    // Skip if no media assets overlap with text
    if (!hasOverlappingMediaAssets(scene, textAsset)) {
        return null;
    }

    // Get the text color
    const textColor = getTextAssetColor(textAsset);

    // Get the background colors
    const backgroundColors = await getBackgroundColorsForTextAsset(scene, textAsset, canvas);

    if (backgroundColors.length === 0) {
        throw new Error('No background colors found');
    }

    // Calculate average background color
    const backgroundColor = averageColor(backgroundColors);

    // Calculate contrast ratio
    const contrastRatio = calculateContrastRatio(textColor, backgroundColor);

    return {
        contrastRatio,
        textColor,
        backgroundColor,
        textAssetId: textAsset.id,
        textAssetName: textAsset.name,
        textContent: textAsset.text || '',
    };
}

/**
 * Internal helper: Calculates the contrast ratio for all text assets in a scene.
 * This operates directly on a scene without isolation.
 *
 * @param scene - The Luminary scene to analyze
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @param options - Options for filtering text assets
 * @returns Promise resolving to array of TextContrastResult for each text asset
 */
export async function _calculateAllTextContrasts(
    scene: Slide,
    canvas: HTMLCanvasElement,
    options: {
        onlyEditable?: boolean;
    } = {},
): Promise<TextContrastResult[]> {
    const { onlyEditable = true } = options;

    // Get all text assets
    let textAssets = scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE);

    // Filter to only editable text if requested
    if (onlyEditable) {
        textAssets = textAssets.filter((asset) => asset.isEditable);
    }

    // Calculate contrast for each text asset
    const results: TextContrastResult[] = [];

    for (const textAsset of textAssets) {
        if (textAsset.text.trim()) {
            const result = await calculateTextContrast(scene, textAsset, canvas);
            if (result !== null) {
                results.push(result);
            }
        }
    }

    return results;
}

/**
 * Calculates the contrast ratio for all text assets in a scene using a pre-created isolated Luminary instance.
 * Use this when processing multiple scenes to avoid creating/destroying WebGL contexts repeatedly.
 *
 * @param isolatedLuminary - A pre-created isolated Luminary instance (caller is responsible for cleanup)
 * @param sceneId - The ID of the scene to analyze
 * @param options - Options for filtering text assets and brightness settings
 * @returns Promise resolving to array of TextContrastResult for each text asset
 */
export async function calculateAllTextContrastsWithLuminary(
    isolatedLuminary: Luminary,
    sceneId: string,
    options: {
        onlyEditable?: boolean;
        setMediaBrightnessToOne?: boolean;
    } = {},
): Promise<TextContrastResult[]> {
    const { setMediaBrightnessToOne = false } = options;

    // Find the scene in the isolated instance by ID
    const isolatedScene = isolatedLuminary.getSceneById(sceneId);

    if (!isolatedScene) {
        throw new Error(`Scene with ID ${sceneId} not found in isolated Luminary instance`);
    }

    // Set all media assets to full brightness if requested
    // This is used to calculate contrast on a "clean slate" to determine optimal brightness
    if (setMediaBrightnessToOne) {
        const mediaAssets = isolatedScene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
        mediaAssets.forEach((asset) => {
            asset.brightness = 1.0;
        });
    }

    // Create a canvas for rendering
    const canvas = document.createElement('canvas');

    // Calculate contrasts using the isolated scene
    return _calculateAllTextContrasts(isolatedScene, canvas, options);
}

/**
 * Calculates the contrast ratio for all text assets in a scene using an isolated Luminary instance.
 * This ensures that the calculations don't affect the main Luminary instance (no magenta text flash).
 *
 * @param luminary - The source Luminary instance (will not be modified)
 * @param sceneId - The ID of the scene to analyze
 * @param options - Options for filtering text assets and brightness settings
 * @returns Promise resolving to array of TextContrastResult for each text asset
 */
export async function calculateAllTextContrasts(
    luminary: Luminary,
    sceneId: string,
    options: {
        onlyEditable?: boolean;
        setMediaBrightnessToOne?: boolean;
    } = {},
): Promise<TextContrastResult[]> {
    // Create an isolated Luminary instance
    const isolatedLuminary = new Luminary({
        fetchResources: {
            audio: false,
            media: true,
            transitions: false,
            fonts: true,
        },
        disableRenderer: false,
        resolutionValue: luminary.resolutionValue,
    });

    try {
        // Load video data into the isolated instance
        const videoData = luminary.save();
        isolatedLuminary.load(videoData);
        await isolatedLuminary.onCanPlay();

        // Use the shared implementation
        const results = await calculateAllTextContrastsWithLuminary(
            isolatedLuminary,
            sceneId,
            options,
        );

        return results;
    } finally {
        // Always destroy the isolated instance to free resources
        await isolatedLuminary.destroy();
    }
}

/**
 * Calculates the contrast ratio for all text assets in all scenes of a Luminary video.
 *
 * @param luminary - The Luminary instance
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @param options - Options for filtering text assets
 * @returns Promise resolving to a map of sceneId -> array of TextContrastResult
 */
export async function calculateAllTextContrastsForVideo(
    luminary: Luminary,
    canvas: HTMLCanvasElement,
    options: {
        onlyEditable?: boolean;
    } = {},
): Promise<Map<string, TextContrastResult[]>> {
    const results = new Map<string, TextContrastResult[]>();

    for (const scene of luminary.scenes) {
        const sceneResults = await _calculateAllTextContrasts(scene, canvas, options);
        if (sceneResults.length > 0) {
            results.set(scene.id, sceneResults);
        }
    }

    return results;
}

/**
 * Extracts colors from ImageData, filtering by alpha channel.
 * Only includes pixels with alpha >= threshold (default 128, ~50% opacity).
 *
 * @param imageData - The ImageData to extract colors from
 * @param sampleRate - Sample every Nth pixel (default 10)
 * @param alphaThreshold - Minimum alpha value to include (0-255, default 128)
 * @returns Array of RGB colors for pixels above alpha threshold
 */
function extractColorsFromImageDataWithAlpha(
    imageData: ImageData,
    sampleRate = 10,
    alphaThreshold = 128,
): RGB[] {
    const colors: RGB[] = [];
    const { data, width, height } = imageData;

    for (let y = 0; y < height; y += sampleRate) {
        for (let x = 0; x < width; x += sampleRate) {
            const index = (y * width + x) * 4;
            const alpha = data[index + 3];

            // Only include pixels with sufficient opacity
            if (alpha >= alphaThreshold) {
                colors.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2],
                });
            }
        }
    }

    return colors;
}

/**
 * Calculates the average color of a watermark by sampling its pixels.
 * Excludes transparent pixels (alpha < 128).
 *
 * @param scene - The scene containing the watermark
 * @param watermark - The watermark MediaAsset
 * @param canvas - Canvas to render to
 * @returns Average RGB color of visible watermark pixels
 */
async function getWatermarkAverageColor(
    scene: Slide,
    watermark: MediaAsset,
    canvas: HTMLCanvasElement,
): Promise<RGB> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    // Render the scene with the watermark
    await scene.renderPreviewToCanvas(canvas, {
        subsceneIndex: 0,
    });

    // Get watermark bounds in output resolution
    const bounds = watermark.getFreelyPositionedParent().approximateMaxBounds;

    // Scale from reference resolution to output resolution
    const scaleX = width / refWidth;
    const scaleY = height / refHeight;

    const x = Math.max(0, Math.floor(bounds.x * scaleX));
    const y = Math.max(0, Math.floor(bounds.y * scaleY));
    const watermarkWidth = Math.min(width - x, Math.ceil(bounds.width * scaleX));
    const watermarkHeight = Math.min(height - y, Math.ceil(bounds.height * scaleY));

    if (watermarkWidth <= 0 || watermarkHeight <= 0) {
        throw new Error('Watermark bounds are out of canvas bounds');
    }

    // Get the canvas context and extract the watermark region
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    const imageData = ctx.getImageData(x, y, watermarkWidth, watermarkHeight);

    // Log watermark patch for debugging
    if (DEBUG_CONTRAST) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = watermarkWidth;
        tempCanvas.height = watermarkHeight;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
            tempCtx.putImageData(imageData, 0, 0);
            const watermarkPatchDataURL = tempCanvas.toDataURL();
            // eslint-disable-next-line no-console
            console.log(
                `[Watermark Contrast] Watermark patch (${watermarkWidth}x${watermarkHeight} at ${x},${y}):`,
                watermarkPatchDataURL,
            );
        }
    }

    // Sample watermark pixels, excluding transparent ones
    const sampleRate = 10;
    const colors = extractColorsFromImageDataWithAlpha(imageData, sampleRate, 128);

    if (colors.length === 0) {
        throw new Error('No visible watermark pixels found');
    }

    // Return average color
    return averageColor(colors);
}

/**
 * Gets the background colors behind a watermark by temporarily hiding it.
 *
 * @param scene - The scene containing the watermark
 * @param watermark - The watermark MediaAsset
 * @param luminary - The Luminary instance
 * @param canvas - Canvas to render to
 * @returns Array of RGB colors sampled from the background
 */
async function getBackgroundColorsForWatermark(
    scene: Slide,
    watermark: MediaAsset,
    luminary: Luminary,
    canvas: HTMLCanvasElement,
): Promise<RGB[]> {
    const { width, height } = scene.root.outputResolution;
    const { width: refWidth, height: refHeight } = scene.root.referenceResolution;

    // Ensure canvas is the correct size
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    // Get watermark bounds
    const bounds = watermark.getFreelyPositionedParent().approximateMaxBounds;

    // Scale from reference resolution to output resolution
    const scaleX = width / refWidth;
    const scaleY = height / refHeight;

    const x = Math.max(0, Math.floor(bounds.x * scaleX));
    const y = Math.max(0, Math.floor(bounds.y * scaleY));
    const watermarkWidth = Math.min(width - x, Math.ceil(bounds.width * scaleX));
    const watermarkHeight = Math.min(height - y, Math.ceil(bounds.height * scaleY));

    if (watermarkWidth <= 0 || watermarkHeight <= 0) {
        throw new Error('Watermark bounds are out of canvas bounds');
    }

    // Temporarily hide the watermark
    const originalVisible = luminary.template.watermark.visible;
    luminary.template.watermark.visible = false;

    try {
        // Render the scene without the watermark
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
        });

        // Get the canvas context
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        // Extract the pixel data from the watermark's bounds
        const imageData = ctx.getImageData(x, y, watermarkWidth, watermarkHeight);

        // Log background patch for debugging
        if (DEBUG_CONTRAST) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = watermarkWidth;
            tempCanvas.height = watermarkHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (tempCtx) {
                tempCtx.putImageData(imageData, 0, 0);
                const backgroundPatchDataURL = tempCanvas.toDataURL();
                // eslint-disable-next-line no-console
                console.log(
                    `[Watermark Contrast] Background patch (${watermarkWidth}x${watermarkHeight} at ${x},${y}):`,
                    backgroundPatchDataURL,
                );
            }
        }

        // Sample every 10th pixel for performance
        const sampleRate = 10;
        const colors = extractColorsFromImageData(imageData, sampleRate);

        return colors;
    } finally {
        // Restore the original watermark visibility
        luminary.template.watermark.visible = originalVisible;
    }
}

/**
 * Internal helper: Calculates the contrast ratio between a watermark and its background in a scene.
 * This operates directly on a scene without isolation.
 *
 * @param scene - The Luminary scene containing the watermark
 * @param luminary - The Luminary instance
 * @param canvas - A canvas element to use for rendering (will be resized as needed)
 * @returns Promise resolving to WatermarkContrastResult with contrast information, or null if no watermark
 */
export async function _calculateWatermarkContrast(
    scene: Slide,
    luminary: Luminary,
    canvas: HTMLCanvasElement,
): Promise<WatermarkContrastResult | null> {
    // Get the watermark asset
    const watermark = scene.getAssetByName('watermark_1');

    if (!watermark || !VideoData.isMediaAsset(watermark)) {
        return null; // No watermark
    }

    // Check if watermark is visible
    if (!luminary.template.watermark.visible) {
        return null;
    }

    // Get watermark color (average of visible pixels)
    const watermarkColor = await getWatermarkAverageColor(scene, watermark, canvas);

    // Get background colors
    const backgroundColors = await getBackgroundColorsForWatermark(
        scene,
        watermark,
        luminary,
        canvas,
    );

    if (backgroundColors.length === 0) {
        throw new Error('No background colors found');
    }

    // Calculate average background color
    const backgroundColor = averageColor(backgroundColors);

    // Calculate contrast ratio
    const contrastRatio = calculateContrastRatio(watermarkColor, backgroundColor);

    // Log final contrast result
    if (DEBUG_CONTRAST) {
        // eslint-disable-next-line no-console
        console.log('[Watermark Contrast] Final result:', {
            contrastRatio: contrastRatio.toFixed(2),
            watermarkColor,
            backgroundColor,
            position: luminary.template.watermark.position,
            opacity: luminary.template.watermark.opacity,
        });
    }

    return {
        contrastRatio,
        watermarkColor,
        backgroundColor,
        watermarkPosition: luminary.template.watermark.position,
        watermarkOpacity: luminary.template.watermark.opacity,
    };
}

/**
 * Calculates the contrast ratio for a watermark in a scene using an isolated Luminary instance.
 * This ensures that the calculations don't affect the main Luminary instance.
 *
 * @param luminary - The source Luminary instance (will not be modified)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to WatermarkContrastResult, or null if no watermark
 */
export async function calculateWatermarkContrast(
    luminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastResult | null> {
    // Create an isolated Luminary instance
    const isolatedLuminary = new Luminary({
        fetchResources: {
            audio: false,
            media: true,
            transitions: false,
            fonts: true,
        },
        disableRenderer: false,
        resolutionValue: luminary.resolutionValue,
    });

    try {
        // Load video data into the isolated instance
        const videoData = luminary.save();
        isolatedLuminary.load(videoData);
        await isolatedLuminary.onCanPlay();

        // Find the scene in the isolated instance by ID
        const isolatedScene = isolatedLuminary.getSceneById(sceneId);

        if (!isolatedScene) {
            throw new Error(`Scene with ID ${sceneId} not found in isolated Luminary instance`);
        }

        // Create a canvas for rendering
        const canvas = document.createElement('canvas');

        // Calculate watermark contrast using the internal helper
        const result = await _calculateWatermarkContrast(isolatedScene, isolatedLuminary, canvas);

        return result;
    } finally {
        // Always destroy the isolated instance to free resources
        await isolatedLuminary.destroy();
    }
}

/**
 * Checks if there is a watermark on the scene that overlaps with any editable media assets.
 * Returns true if watermark exists, is visible, and intersects with at least one editable media asset.
 */
function hasWatermarkOverlappingMediaAssets(scene: Slide, luminary: Luminary): boolean {
    // Check if watermark exists and is visible
    if (!luminary.template.watermark.visible) {
        return false;
    }

    // Get the watermark asset
    const watermark = scene.getAssetByName('watermark_1');
    if (!watermark || !VideoData.isMediaAsset(watermark)) {
        return false;
    }

    // Get watermark bounds
    const watermarkBounds = watermark.getFreelyPositionedParent().approximateMaxBounds;
    const watermarkLeft = Math.floor(watermarkBounds.x);
    const watermarkRight = Math.ceil(watermarkBounds.x + watermarkBounds.width);
    const watermarkTop = Math.floor(watermarkBounds.y);
    const watermarkBottom = Math.ceil(watermarkBounds.y + watermarkBounds.height);

    // Get all editable media assets
    const mediaAssets = scene
        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
        .filter((asset) => asset.isEditable);

    // Check if watermark overlaps with any media asset
    for (const mediaAsset of mediaAssets) {
        const mediaBounds = mediaAsset.getFreelyPositionedParent().approximateMaxBounds;
        const mediaLeft = Math.floor(mediaBounds.x);
        const mediaRight = Math.ceil(mediaBounds.x + mediaBounds.width);
        const mediaTop = Math.floor(mediaBounds.y);
        const mediaBottom = Math.ceil(mediaBounds.y + mediaBounds.height);

        // Check for overlap using rectangle intersection
        const deltaX = Math.min(watermarkRight, mediaRight) - Math.max(watermarkLeft, mediaLeft);
        const deltaY = Math.min(watermarkBottom, mediaBottom) - Math.max(watermarkTop, mediaTop);
        const overlaps = deltaX > 0 && deltaY > 0;

        if (overlaps) {
            return true;
        }
    }

    return false;
}

/**
 * Result from testing multiple watermark options for contrast.
 */
export interface WatermarkContrastOption {
    watermarkIndex: number;
    contrastResult: WatermarkContrastResult;
}

/**
 * Calculates contrast for all available watermark options using a pre-created isolated Luminary instance.
 * Use this when processing multiple scenes to avoid creating/destroying WebGL contexts repeatedly.
 *
 * @param isolatedLuminary - A pre-created isolated Luminary instance (caller is responsible for cleanup)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to array of contrast results for each watermark option, or empty array if not applicable
 */
export async function calculateWatermarkContrastsForAllOptionsWithLuminary(
    isolatedLuminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastOption[]> {
    // Only calculate if template has exactly 2 watermarks
    if (isolatedLuminary.template.watermarkSources.length !== 2) {
        return [];
    }

    // Find the scene in the isolated instance by ID
    const isolatedScene = isolatedLuminary.getSceneById(sceneId);

    if (!isolatedScene) {
        return [];
    }

    // Only calculate if watermark exists and overlaps with editable media
    if (!hasWatermarkOverlappingMediaAssets(isolatedScene, isolatedLuminary)) {
        return [];
    }

    // Create a canvas for rendering
    const canvas = document.createElement('canvas');

    // Calculate contrast for each watermark option
    const results: WatermarkContrastOption[] = [];

    for (let watermarkIndex = 0; watermarkIndex < 2; watermarkIndex++) {
        // Set the watermark to test
        isolatedScene.watermark.sourceData =
            VideoData.templateWatermarkSourcesByIndex(watermarkIndex);

        // Calculate contrast for this watermark
        const contrastResult = await _calculateWatermarkContrast(
            isolatedScene,
            isolatedLuminary,
            canvas,
        );

        // Only include results where contrast was successfully calculated
        if (contrastResult !== null) {
            results.push({
                watermarkIndex,
                contrastResult,
            });
        }
    }

    return results;
}

/**
 * Calculates contrast for all available watermark options in a template.
 * Only works with templates that have exactly 2 watermarks, a visible watermark on the scene,
 * and the watermark overlaps with at least one editable media asset.
 *
 * @param luminary - The source Luminary instance (will not be modified)
 * @param sceneId - The ID of the scene to analyze
 * @returns Promise resolving to array of contrast results for each watermark option, or empty array if not applicable
 */
export async function calculateWatermarkContrastsForAllOptions(
    luminary: Luminary,
    sceneId: string,
): Promise<WatermarkContrastOption[]> {
    // Only calculate if template has exactly 2 watermarks
    if (luminary.template.watermarkSources.length !== 2) {
        return [];
    }

    // Find the scene to check for watermark overlap
    const scene = luminary.scenes.find((s) => s.id === sceneId);
    if (!scene) {
        return [];
    }

    // Only calculate if watermark exists and overlaps with editable media
    if (!hasWatermarkOverlappingMediaAssets(scene, luminary)) {
        return [];
    }

    // Create an isolated Luminary instance
    const isolatedLuminary = new Luminary({
        fetchResources: {
            audio: false,
            media: true,
            transitions: false,
            fonts: true,
        },
        disableRenderer: false,
        resolutionValue: luminary.resolutionValue,
    });

    try {
        // Load video data into the isolated instance
        const videoData = luminary.save();
        isolatedLuminary.load(videoData);
        await isolatedLuminary.onCanPlay();

        // Use the shared implementation
        return calculateWatermarkContrastsForAllOptionsWithLuminary(isolatedLuminary, sceneId);
    } finally {
        // Always destroy the isolated instance to free resources
        await isolatedLuminary.destroy();
    }
}

/**
 * Selects the best watermark index from contrast calculation results.
 * Returns the watermark with the highest contrast ratio.
 *
 * @param results - Array of watermark contrast options
 * @returns The index of the watermark with best contrast, or null if no valid results
 */
export function selectBestWatermarkIndexByContrast(
    results: WatermarkContrastOption[],
): number | null {
    if (results.length === 0) {
        return null;
    }

    // Find watermark with highest contrast ratio
    let bestIndex: number | null = null;
    let bestContrast = -1;

    for (const option of results) {
        if (option.contrastResult.contrastRatio > bestContrast) {
            bestContrast = option.contrastResult.contrastRatio;
            bestIndex = option.watermarkIndex;
        }
    }

    return bestIndex;
}

// Expose utility for debugging in browser console
if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).calculateAllTextContrastsForVideo = calculateAllTextContrastsForVideo;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).calculateWatermarkContrast = calculateWatermarkContrast;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).calculateWatermarkContrastsForAllOptions =
        calculateWatermarkContrastsForAllOptions;
}
